import React, { useCallback, useEffect, useState } from "react";
import { Box, Paper, Button, IconButton, Typography } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { useSnackbar } from "notistack";
import { useNavigate, useLocation } from "react-router-dom";
import { useSelector } from "react-redux";
import CandidateTable from "./candidate/CandidateTable";
import sh from "bundles/common/utils/sh";
import NotesDrawer from "bundles/Internal/components/common/NotesDrawer";
import { useCandidateModal } from "bundles/Internal/components/common/CandidateModalProvider";
import ApplyToOfferDialog from "bundles/Internal/components/common/ApplyToOfferDialog";
import { appendFormValue } from "bundles/Internal/utils/candidateFormData";

const CREATE_DEFAULTS = { country: "france" };

const FrenchMarketCandidatesPage = () => {
  const [candidates, setCandidates] = useState([]);
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const location = useLocation();
  const authenticity_token = useSelector((state) => state.authenticity_token);
  const { openCreateCandidate } = useCandidateModal();
  const [notesOpen, setNotesOpen] = useState(false);
  const [notesCandidate, setNotesCandidate] = useState(null);
  const [searchQuery, setSearchQuery] = useState("");
  const [page, setPage] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [loading, setLoading] = useState(false);
  const [selectedCandidateIds, setSelectedCandidateIds] = useState([]);
  const [applyToJobDialogOpen, setApplyToJobDialogOpen] = useState(false);

  const fetchCandidates = useCallback((query = "", pageNum = 1, limit = 25) => {
    setLoading(true);
    sh.get("/internal/candidates", {
      params: {
        scope: "french_market",
        query: String(query || "").trim() || undefined,
        page: pageNum,
        limit,
      },
    })
      .then((res) => {
        const payload = res.data?.data || [];
        setCandidates(payload);
        setTotalCount(res.data?.pagy?.count ?? payload.length);
      })
      .catch((err) => {
        if (err?.code === "ERR_CANCELED" || String(err?.message || "").toLowerCase().includes("canceled")) return;
        enqueueSnackbar("Failed to fetch French market candidates", { variant: "error" });
      })
      .finally(() => setLoading(false));
  }, [enqueueSnackbar]);

  useEffect(() => {
    const t = setTimeout(() => {
      setPage(1);
      fetchCandidates(searchQuery, 1, rowsPerPage);
    }, 250);
    return () => clearTimeout(t);
  }, [fetchCandidates, searchQuery, rowsPerPage]);

  const handlePageChange = useCallback((newPage) => {
    setPage(newPage);
    fetchCandidates(searchQuery, newPage, rowsPerPage);
  }, [fetchCandidates, searchQuery, rowsPerPage]);

  const handleRowsPerPageChange = useCallback((newRowsPerPage) => {
    setRowsPerPage(newRowsPerPage);
    setPage(1);
  }, []);

  const handleSelectCandidate = useCallback((candidateId, checked) => {
    setSelectedCandidateIds((prev) =>
      checked ? [...prev, candidateId] : prev.filter((id) => id !== candidateId)
    );
  }, []);

  const handleSelectAll = useCallback((checked) => {
    setSelectedCandidateIds(checked ? candidates.map((c) => c.id) : []);
  }, [candidates]);

  const handleApplySuccess = useCallback(() => {
    setSelectedCandidateIds([]);
    setApplyToJobDialogOpen(false);
  }, []);

  const handleCreateSuccess = (reset, close) => {
    enqueueSnackbar("Candidate created", { variant: "success" });
    reset?.();
    close?.();
    fetchCandidates(searchQuery, page, rowsPerPage);
  };

  const handleCreateError = (err) => {
    const messages = err?.response?.data?.errors;
    if (err?.response?.status === 422 && Array.isArray(messages) && messages.length > 0) {
      messages.forEach((msg) => enqueueSnackbar(msg, { variant: "error" }));
      return;
    }
    enqueueSnackbar("Failed to create candidate", { variant: "error" });
  };

  const createCandidate = (values, { reset, resumeFile, autoEmail, close } = {}) => {
    if (!resumeFile) {
      return sh.post("/internal/candidates", {
        candidate: values,
        authenticity_token,
        auto_email: autoEmail,
      })
        .then(() => handleCreateSuccess(reset, close))
        .catch(handleCreateError);
    }

    const formData = new FormData();
    formData.append("authenticity_token", authenticity_token);

    const appendValue = (key, value) => appendFormValue(formData, key, value);

    Object.entries(values || {}).forEach(([k, v]) => appendValue(`candidate[${k}]`, v));
    formData.append("candidate[resume]", resumeFile);

    if (autoEmail && typeof autoEmail === "object") {
      Object.entries(autoEmail).forEach(([k, v]) => appendValue(`auto_email[${k}]`, v));
    }

    return sh.post("/internal/candidates", formData)
      .then(() => handleCreateSuccess(reset, close))
      .catch(handleCreateError);
  };

  return (
    <Box
      sx={{
        minHeight: "100vh",
        background: "linear-gradient(180deg, #F9FAFF 0%, #EEF0F6 100%)",
      }}
    >
      <CandidateTable
        candidates={candidates}
        onOpenCreateCandidate={() => openCreateCandidate({ onSubmit: createCandidate, defaultValues: CREATE_DEFAULTS })}
        onApplyToJob={() => setApplyToJobDialogOpen(true)}
        onQueryChange={(q) => setSearchQuery(q)}
        onOpenNotes={(candidate) => {
          setNotesCandidate(candidate);
          setNotesOpen(true);
        }}
        onCandidateSelect={(candidate) => {
          if (!candidate?.id) return;
          navigate(`/internal/candidates/${candidate.id}`, { state: { candidate, from: location.pathname } });
        }}
        page={page}
        totalCount={totalCount}
        rowsPerPage={rowsPerPage}
        onPageChange={handlePageChange}
        onRowsPerPageChange={handleRowsPerPageChange}
        loading={loading}
        selectedCandidateIds={selectedCandidateIds}
        onSelectCandidate={handleSelectCandidate}
        onSelectAll={handleSelectAll}
      />

      {selectedCandidateIds.length > 0 && (
        <Box
          sx={{
            position: "fixed",
            bottom: 24,
            left: "50%",
            transform: "translateX(-50%)",
            zIndex: 1000,
          }}
        >
          <Paper
            elevation={8}
            sx={{
              px: 3,
              py: 1.5,
              display: "flex",
              gap: 1.5,
              alignItems: "center",
              borderRadius: "50px",
              backgroundColor: "#fff",
              border: "1px solid hsl(140 10% 88%)",
            }}
          >
            <Typography sx={{ color: "hsl(150 30% 12%)", fontFamily: "Segoe UI Semibold, system-ui, sans-serif", fontSize: 14, mr: 1 }}>
              {selectedCandidateIds.length} selected
            </Typography>
            <Box sx={{ width: "1px", height: 24, backgroundColor: "hsl(140 10% 88%)" }} />
            <Button
              size="small"
              variant="contained"
              onClick={() => setApplyToJobDialogOpen(true)}
              sx={{
                textTransform: "none",
                borderRadius: "20px",
                backgroundColor: "hsl(150 38% 17%)",
                "&:hover": { backgroundColor: "hsl(150 38% 22%)" },
              }}
            >
              Apply to Job
            </Button>
            <IconButton
              size="small"
              onClick={() => setSelectedCandidateIds([])}
              sx={{ ml: 0.5 }}
            >
              <CloseIcon fontSize="small" />
            </IconButton>
          </Paper>
        </Box>
      )}

      <ApplyToOfferDialog
        open={applyToJobDialogOpen}
        onClose={() => setApplyToJobDialogOpen(false)}
        candidateIds={selectedCandidateIds}
        onApplied={handleApplySuccess}
      />


      <NotesDrawer
        open={notesOpen}
        onClose={() => setNotesOpen(false)}
        resource="candidates"
        record={notesCandidate}
        onNoteAdded={(candidateId) => {
          setCandidates((prev) =>
            prev.map((c) =>
              String(c?.id) === String(candidateId)
                ? { ...c, notes_count: (Number(c.notes_count) || 0) + 1 }
                : c
            )
          );
        }}
        onNoteArchived={(candidateId) => {
          setCandidates((prev) =>
            prev.map((c) =>
              String(c?.id) === String(candidateId)
                ? { ...c, notes_count: Math.max((Number(c.notes_count) || 0) - 1, 0) }
                : c
            )
          );
        }}
      />
    </Box>
  );
};

export default FrenchMarketCandidatesPage;
